import { useState } from "react";
import { Plus } from "lucide-react";
import { Button, Tabs } from "../components/ui";
import type { TabItem } from "../components/ui";
import { useAuth } from "../hooks/useAuth";
import { useDiscoverReports, useReportsList } from "../hooks";
import { BrianToggleButton } from "../components/Brian";
import {
  DiscoverPanel,
  MyReportsPanel,
  ReportFormModal,
  ReportsBrianBuilder,
  ScheduleModal,
  SubscriptionsPanel,
} from "../components/Reports";
import type { ReportFilter } from "../components/Reports";
import type { GeneratedReportProposal } from "../api/userReports";
import type { UserReport } from "../types/reports";

type ReportsTab = "mine" | "discover" | "subscriptions";

const TABS: TabItem<ReportsTab>[] = [
  { value: "mine", label: "My reports" },
  { value: "discover", label: "Discover" },
  { value: "subscriptions", label: "Subscriptions" },
];

export default function ReportsPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<ReportsTab>("mine");
  const [reportFilter, setReportFilter] = useState<ReportFilter>("all");
  const [showCreate, setShowCreate] = useState(false);
  const [showBrian, setShowBrian] = useState(false);
  const [aiPrefill, setAiPrefill] = useState<GeneratedReportProposal | null>(null);
  const [manageSchedule, setManageSchedule] = useState<UserReport | null>(null);

  const list = useReportsList(user?.id);
  const discover = useDiscoverReports(activeTab === "discover");

  async function refreshAll() {
    await Promise.all([list.reload(), discover.reload()]);
  }

  async function handleReportCreated(savedReport?: UserReport) {
    setShowCreate(false);
    setAiPrefill(null);
    setShowBrian(false);
    setActiveTab("mine");
    setReportFilter("created");
    await list.reload();
    if (savedReport?.id) {
      window.requestAnimationFrame(() => {
        document.getElementById(`report-${savedReport.id}`)?.focus();
      });
    }
  }

  function openManualCreate() {
    setAiPrefill(null);
    setShowCreate(true);
  }

  function reviewBrianProposal(proposal: GeneratedReportProposal) {
    setAiPrefill(proposal);
    setShowCreate(true);
  }

  return (
    <div className="p-4 md:p-6">
      <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <h1 className="text-lg font-semibold text-gray-800">Reports</h1>
        <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
          <BrianToggleButton open={showBrian} onToggle={() => setShowBrian((open) => !open)} />
          <Button onClick={openManualCreate}>
            <Plus size={15} aria-hidden="true" />
            New report
          </Button>
        </div>
      </div>

      <Tabs
        tabs={TABS}
        value={activeTab}
        onChange={setActiveTab}
        idPrefix="reports"
        ariaLabel="Report views"
      />

      {showBrian && (
        <ReportsBrianBuilder
          onClose={() => setShowBrian(false)}
          onReview={reviewBrianProposal}
          onCreated={handleReportCreated}
        />
      )}

      <section
        id="reports-panel-mine"
        role="tabpanel"
        aria-labelledby="reports-tab-mine"
        hidden={activeTab !== "mine"}
      >
        <MyReportsPanel
          reports={list.reports}
          createdByMe={list.createdByMe}
          subscribed={list.subscribed}
          sharedWithMe={list.sharedWithMe}
          loading={list.loading}
          error={list.error}
          filter={reportFilter}
          onFilterChange={setReportFilter}
          onRefresh={list.reload}
          onCreate={openManualCreate}
          onDiscover={() => setActiveTab("discover")}
        />
      </section>

      <section
        id="reports-panel-discover"
        role="tabpanel"
        aria-labelledby="reports-tab-discover"
        hidden={activeTab !== "discover"}
      >
        <DiscoverPanel
          page={discover.page}
          loading={discover.loading}
          error={discover.error}
          search={discover.search}
          onSearchChange={discover.setSearch}
          currentPage={discover.currentPage}
          onPageChange={discover.setCurrentPage}
          pageSize={discover.pageSize}
          onSubscriptionChange={refreshAll}
        />
      </section>

      <section
        id="reports-panel-subscriptions"
        role="tabpanel"
        aria-labelledby="reports-tab-subscriptions"
        hidden={activeTab !== "subscriptions"}
      >
        <SubscriptionsPanel
          active={activeTab === "subscriptions"}
          myReports={list.createdByMe}
          onManageSchedule={setManageSchedule}
        />
      </section>

      {showCreate && (
        <ReportFormModal
          prefill={aiPrefill}
          onClose={() => {
            setShowCreate(false);
            setAiPrefill(null);
          }}
          onSuccess={handleReportCreated}
        />
      )}

      {manageSchedule && (
        <ScheduleModal
          report={manageSchedule}
          onClose={() => setManageSchedule(null)}
          onSuccess={() => { setManageSchedule(null); void list.reload(); }}
        />
      )}
    </div>
  );
}
